<template>
  <div>
    <div class="nk-app-root">
      <div class="nk-main">
        <div class="nk-wrap">
          <Header v-show="!loading_page" />
          <div class="nk-content">
            <div class="container wide-xl">
              <div class="nk-content-inner">
                <Navigation v-show="!loading_page" />
                <div class="nk-content-body" v-show="!loading_page">
                  <div class="nk-content-wrap">
                    <div class="nk-block nk-block-lg">
                      <div class="nk-block-head">
                        <div class="nk-block-head-content d-flex justify-content-between align-items-center">
                          <div>
                            <h4 class="nk-block-title">{{ $t('dm_preislisten_title') }}</h4>
                            <span class="text-muted small" v-if="preisliste.version">Version {{ preisliste.version }}</span>
                          </div>
                          <div class="d-flex align-items-center" v-if="loaded && dmBerechtigungen.preisliste_bearbeiten">
                            <button class="btn btn-sm btn-outline-secondary mr-2" @click="$refs.reimportFile.click()">
                              <em class="icon ni ni-upload"></em> {{ isNewFormat ? $t('dm_preislisten_btn_reimport') : $t('dm_preislisten_btn_import') }}
                            </button>
                            <input type="file" ref="reimportFile" accept=".xlsm,.xlsx" style="display:none" @change="onReimport" />
                            <button v-if="isNewFormat && !preisliste.freigegeben" class="btn btn-sm btn-primary" @click="savePreisliste">
                              <em class="icon ni ni-save"></em> {{ $t('dm_common_btn_save') }}
                            </button>
                          </div>
                        </div>
                      </div>

                      <div class="alert alert-danger" v-if="errorMessage">{{ errorMessage }}</div>
                      <div class="alert alert-success" v-if="successMessage">{{ successMessage }}</div>

                      <!-- Old format fallback -->
                      <div v-if="loaded && !isNewFormat" class="alert alert-warning">
                        {{ $t('dm_preislisten_old_format_hint') }}
                      </div>

                      <!-- New format: Tab-based detail -->
                      <div v-if="loaded && isNewFormat">
                        <ul class="nav nav-tabs">
                          <li class="nav-item" v-for="tab in tabs" :key="tab.key">
                            <a class="nav-link" :class="{ active: activeTab === tab.key }" href="#" @click.prevent="activeTab = tab.key">
                              {{ tab.label }}
                            </a>
                          </li>
                        </ul>

                        <div class="card card-bordered" style="border-top: none; border-top-left-radius: 0; border-top-right-radius: 0;">
                          <div class="card-inner">

                            <!-- Tab: Berechnung -->
                            <div v-show="activeTab === 'berechnung'">
                              <div v-for="(group, gIdx) in berechnungGroups" :key="gIdx" class="mb-4">
                                <div class="dm-group-header" @click="group.collapsed = !group.collapsed">
                                  <em class="icon ni" :class="group.collapsed ? 'ni-chevron-right' : 'ni-chevron-down'"></em>
                                  <strong>{{ group.produkt }}</strong>
                                  <span class="text-muted ml-2">({{ group.items.length }})</span>
                                </div>
                                <div v-show="!group.collapsed">
                                  <div class="dm-table-wrap">
                                    <table class="dm-table dm-table-compact">
                                      <thead>
                                        <tr>
                                          <th style="width: 18%">{{ $t('dm_pl_col_modul') }}</th>
                                          <th style="width: 18%">{{ $t('dm_pl_col_detail') }}</th>
                                          <th style="width: 12%">{{ $t('dm_pl_col_formel') }}</th>
                                          <th style="width: 6%">{{ $t('dm_pl_col_faktor') }}</th>
                                          <th style="width: 6%">{{ $t('dm_pl_col_basis') }}</th>
                                          <th style="width: 5%">IBN</th>
                                          <th style="width: 5%">EoW</th>
                                          <th style="width: 5%">WKP-b</th>
                                          <th style="width: 5%">WKP+</th>
                                          <th style="width: 5%">ZOP</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        <tr v-for="(item, iIdx) in group.items" :key="iIdx">
                                          <td>
                                            <input v-if="!readOnly" type="text" class="form-control form-control-sm" v-model="item.modul" />
                                            <span v-else>{{ item.modul }}</span>
                                          </td>
                                          <td>
                                            <input v-if="!readOnly" type="text" class="form-control form-control-sm" v-model="item.detail" />
                                            <span v-else>{{ item.detail }}</span>
                                          </td>
                                          <td>
                                            <select v-if="!readOnly" class="form-control form-control-sm" v-model="item.formel_typ">
                                              <option v-for="ft in formelTypen" :key="ft.value" :value="ft.value">{{ ft.label }}</option>
                                            </select>
                                            <span v-else class="small">{{ formelTypLabel(item.formel_typ) }}</span>
                                          </td>
                                          <td>
                                            <input v-if="!readOnly && formelHasFaktor(item.formel_typ)" type="number" step="0.01" class="form-control form-control-sm" v-model.number="item.formel_faktor" />
                                            <span v-else-if="formelHasFaktor(item.formel_typ)" class="small">{{ item.formel_faktor }}</span>
                                            <span v-else class="text-muted">—</span>
                                          </td>
                                          <td>
                                            <input v-if="!readOnly && formelHasBasis(item.formel_typ)" type="number" step="0.01" class="form-control form-control-sm" v-model.number="item.formel_basis" />
                                            <span v-else-if="formelHasBasis(item.formel_typ)" class="small">{{ item.formel_basis }}</span>
                                            <span v-else class="text-muted">—</span>
                                          </td>
                                          <td v-for="pt in pruefTypKeys" :key="pt">
                                            <select v-if="!readOnly" class="form-control form-control-sm dm-select-mini" v-model="item.pruefungstypen[pt]">
                                              <option value="">—</option>
                                              <option value="x">x</option>
                                              <option value="ggf">ggf</option>
                                            </select>
                                            <span v-else :class="{'text-success fw-bold': item.pruefungstypen[pt] === 'x', 'text-warning': item.pruefungstypen[pt] === 'ggf'}">
                                              {{ item.pruefungstypen[pt] || '—' }}
                                            </span>
                                          </td>
                                        </tr>
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <!-- Tab: Anlagendaten -->
                            <div v-show="activeTab === 'anlagendaten'">
                              <div class="d-flex justify-content-between align-items-center mb-3">
                                <div class="d-flex align-items-center">
                                  <select class="form-control form-control-sm mr-2" v-model="anlagenFilter" style="width: 200px;">
                                    <option value="">{{ $t('dm_common_all') }}</option>
                                    <option v-for="h in herstellerList" :key="h" :value="h">{{ h }}</option>
                                  </select>
                                  <input type="text" class="form-control form-control-sm" :placeholder="$t('dm_common_quick_filter')" v-model="anlagenSearch" style="width: 200px;" />
                                </div>
                              </div>
                              <div class="d-flex justify-content-end mb-2" v-if="!readOnly">
                                <button class="btn btn-sm btn-outline-secondary" @click="addAnlage">
                                  <em class="icon ni ni-plus"></em> {{ $t('dm_pl_btn_add_row') }}
                                </button>
                              </div>
                              <div class="dm-table-wrap" style="max-height: calc(100vh - 340px);">
                                <table class="dm-table dm-table-compact">
                                  <thead>
                                    <tr>
                                      <th @click="sortAnlagen('hersteller')" class="dm-table-th">{{ $t('dm_pl_col_hersteller') }} <span v-if="anlagenSort.field==='hersteller'" class="dm-sort-icon">{{ anlagenSort.asc ? '&#9650;' : '&#9660;' }}</span></th>
                                      <th @click="sortAnlagen('typ')" class="dm-table-th">{{ $t('dm_pl_col_typ') }} <span v-if="anlagenSort.field==='typ'" class="dm-sort-icon">{{ anlagenSort.asc ? '&#9650;' : '&#9660;' }}</span></th>
                                      <th @click="sortAnlagen('durchmesser')" class="dm-table-th">{{ $t('dm_pl_col_durchmesser') }}</th>
                                      <th @click="sortAnlagen('kw')" class="dm-table-th">kW</th>
                                      <th class="dm-table-th">{{ $t('dm_pl_col_turmtyp') }}</th>
                                      <th class="dm-table-th">{{ $t('dm_pl_col_trafo') }}</th>
                                      <th class="dm-table-th">{{ $t('dm_pl_col_pruefzeit') }}</th>
                                      <th class="dm-table-th">{{ $t('dm_pl_col_getriebe') }}</th>
                                      <th class="dm-table-th">{{ $t('dm_pl_col_blitzschutz') }}</th>
                                      <th class="dm-table-th">{{ $t('dm_pl_col_fp_rb_aussen') }}</th>
                                      <th class="dm-table-th">{{ $t('dm_pl_col_fp_rb_innen') }}</th>
                                      <th class="dm-table-th">{{ $t('dm_pl_col_nabenhoehen') }}</th>
                                      <th v-if="!readOnly" style="width: 40px;"></th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr v-for="(a, aIdx) in paginatedAnlagen" :key="aIdx">
                                      <td>
                                        <input v-if="!readOnly" type="text" class="form-control form-control-sm" v-model="a.hersteller" />
                                        <span v-else>{{ a.hersteller }}</span>
                                      </td>
                                      <td>
                                        <input v-if="!readOnly" type="text" class="form-control form-control-sm" v-model="a.typ" />
                                        <span v-else>{{ a.typ }}</span>
                                      </td>
                                      <td>
                                        <input v-if="!readOnly" type="number" class="form-control form-control-sm" v-model.number="a.durchmesser" />
                                        <span v-else>{{ a.durchmesser }}</span>
                                      </td>
                                      <td>
                                        <input v-if="!readOnly" type="number" class="form-control form-control-sm" v-model.number="a.kw" />
                                        <span v-else>{{ a.kw }}</span>
                                      </td>
                                      <td>
                                        <select v-if="!readOnly" class="form-control form-control-sm" v-model="a.turmtyp">
                                          <option value="">—</option>
                                          <option value="Steel">Steel</option>
                                          <option value="Hybrid">Hybrid</option>
                                          <option value="Lattice">Lattice</option>
                                        </select>
                                        <span v-else>{{ a.turmtyp || '—' }}</span>
                                      </td>
                                      <td>
                                        <input v-if="!readOnly" type="text" class="form-control form-control-sm" v-model="a.trafo_position" />
                                        <span v-else>{{ a.trafo_position || '—' }}</span>
                                      </td>
                                      <td>
                                        <input v-if="!readOnly" type="number" step="0.01" class="form-control form-control-sm" v-model.number="a.pruefzeit" />
                                        <span v-else>{{ a.pruefzeit }}</span>
                                      </td>
                                      <td>
                                        <select v-if="!readOnly" class="form-control form-control-sm" v-model="a.getriebe">
                                          <option value="y">y</option>
                                          <option value="n">n</option>
                                        </select>
                                        <span v-else>{{ a.getriebe }}</span>
                                      </td>
                                      <td>
                                        <input v-if="!readOnly" type="number" class="form-control form-control-sm" v-model.number="a.blitzschutz_pauschale" />
                                        <span v-else>{{ a.blitzschutz_pauschale }}</span>
                                      </td>
                                      <td>
                                        <input v-if="!readOnly" type="number" class="form-control form-control-sm" v-model.number="a.festpreis_rb_aussen" />
                                        <span v-else>{{ a.festpreis_rb_aussen }}</span>
                                      </td>
                                      <td>
                                        <input v-if="!readOnly" type="number" class="form-control form-control-sm" v-model.number="a.festpreis_rb_innen" />
                                        <span v-else>{{ a.festpreis_rb_innen }}</span>
                                      </td>
                                      <td>
                                        <input v-if="!readOnly" type="text" class="form-control form-control-sm" :value="(a.nabenhoehen || []).join(', ')" @change="parseNabenhoehen(a, $event)" />
                                        <span v-else>{{ (a.nabenhoehen || []).join(', ') }}</span>
                                      </td>
                                      <td v-if="!readOnly">
                                        <button class="btn btn-sm btn-icon btn-outline-danger" @click="removeAnlage(a)">
                                          <em class="icon ni ni-trash"></em>
                                        </button>
                                      </td>
                                    </tr>
                                    <tr v-if="paginatedAnlagen.length === 0">
                                      <td colspan="12" class="text-center text-muted py-4">{{ $t('dm_common_no_entries_found') }}</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                              <!-- Pagination -->
                              <div class="d-flex justify-content-between align-items-center mt-3" v-if="anlagenPageCount > 1">
                                <span class="text-muted small">{{ filteredAnlagen.length }} {{ $t('dm_pl_entries') }}</span>
                                <ul class="pagination pagination-sm">
                                  <li class="page-item" :class="{ disabled: anlagenPage === 1 }">
                                    <a class="page-link" href="#" @click.prevent="anlagenPage--">&laquo;</a>
                                  </li>
                                  <li class="page-item" v-for="p in anlagenPageCount" :key="p" :class="{ active: anlagenPage === p }">
                                    <a class="page-link" href="#" @click.prevent="anlagenPage = p">{{ p }}</a>
                                  </li>
                                  <li class="page-item" :class="{ disabled: anlagenPage === anlagenPageCount }">
                                    <a class="page-link" href="#" @click.prevent="anlagenPage++">&raquo;</a>
                                  </li>
                                </ul>
                              </div>
                            </div>

                            <!-- Tab: Einzelpreise -->
                            <div v-show="activeTab === 'einzelpreise'">
                              <div v-for="(ep, epIdx) in einzelpreise" :key="epIdx" class="mb-4">
                                <div class="d-flex justify-content-between align-items-center mb-2">
                                  <h6 class="mb-0">
                                    <input v-if="!readOnly" type="text" class="form-control form-control-sm d-inline-block" style="width: auto; min-width: 300px;" v-model="ep.bezeichnung" />
                                    <span v-else>{{ ep.bezeichnung }}</span>
                                  </h6>
                                </div>
                                <div class="dm-table-wrap">
                                  <table class="dm-table dm-table-compact">
                                    <thead>
                                      <tr>
                                        <th>{{ $t('dm_pl_col_anzahl') }}</th>
                                        <th>{{ $t('dm_pl_col_basispreis') }}</th>
                                        <th>NH &ge; 100m</th>
                                        <th>NH &ge; 130m</th>
                                        <th>RD &ge; 100m</th>
                                        <th>RD &ge; 130m</th>
                                        <th>RD &ge; 150m</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      <tr v-for="(s, sIdx) in ep.staffeln" :key="sIdx">
                                        <td>
                                          <input v-if="!readOnly" type="number" class="form-control form-control-sm" v-model.number="s.anzahl" />
                                          <span v-else>{{ s.anzahl }}</span>
                                        </td>
                                        <td>
                                          <input v-if="!readOnly" type="number" class="form-control form-control-sm" v-model.number="s.basispreis" />
                                          <span v-else>{{ formatPrice(s.basispreis) }}</span>
                                        </td>
                                        <td v-for="zk in zuschlagKeys" :key="zk">
                                          <input v-if="!readOnly" type="number" class="form-control form-control-sm" v-model.number="s.zuschlaege[zk]" />
                                          <span v-else>{{ formatPrice(s.zuschlaege[zk]) }}</span>
                                        </td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </div>

                            <!-- Tab: Nebenkosten -->
                            <div v-show="activeTab === 'nebenkosten'">
                              <div class="row gy-4" style="max-width: 500px;">
                                <div class="col-12" v-for="nk in nebenkostenFields" :key="nk.key">
                                  <div class="form-group">
                                    <label class="form-label">{{ nk.label }} <span class="text-muted small">({{ nk.unit }})</span></label>
                                    <input v-if="!readOnly" type="number" :step="nk.step || 1" class="form-control" v-model.number="nebenkostenSaetze[nk.key]" />
                                    <div v-else class="form-control-plaintext">{{ nebenkostenSaetze[nk.key] }} {{ nk.unit }}</div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <!-- Tab: Berechnungssätze -->
                            <div v-show="activeTab === 'berechnungssaetze'">
                              <div class="row gy-4" style="max-width: 500px;">
                                <div class="col-sm-6">
                                  <div class="form-group">
                                    <label class="form-label">{{ $t('dm_pl_label_stundensatz') }} (EUR)</label>
                                    <input v-if="!readOnly" type="number" class="form-control" v-model.number="berechnungsSaetze.stundensatz" />
                                    <div v-else class="form-control-plaintext">{{ berechnungsSaetze.stundensatz }} EUR</div>
                                  </div>
                                </div>
                                <div class="col-sm-6">
                                  <div class="form-group">
                                    <label class="form-label">{{ $t('dm_pl_label_tagessatz') }} (EUR)</label>
                                    <input v-if="!readOnly" type="number" class="form-control" v-model.number="berechnungsSaetze.tagessatz" />
                                    <div v-else class="form-control-plaintext">{{ berechnungsSaetze.tagessatz }} EUR</div>
                                  </div>
                                </div>
                              </div>
                            </div>

                          </div>
                        </div>
                      </div>

                    </div>
                  </div>
                  <Footer />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Header from "../../../components/Header.vue";
import Navigation from "../../../components/Navigation.vue";
import Footer from "../../../components/Footer.vue";
import dmPermissionsMixin from "@/mixins/dm-permissions-mixin";
import { getPreisliste, updatePreisliste, importPreisliste } from "@/services/dm.service";

export default {
  name: "DmPreislisteDetail",
  components: { Header, Navigation, Footer },
  mixins: [dmPermissionsMixin],
  data() {
    return {
      preisliste: {},
      loaded: false,
      activeTab: "berechnung",
      errorMessage: "",
      successMessage: "",
      // Berechnung
      berechnung: [],
      berechnungGroups: [],
      pruefTypKeys: ["ibn", "eow", "wkp_basic", "wkp_plus", "zop"],
      formelTypen: [
        { value: "festpreis", label: "Festpreis" },
        { value: "stundensatz_faktor", label: "× Stundensatz" },
        { value: "nh_faktor", label: "× Nabenhöhe" },
        { value: "rd_faktor", label: "× Durchmesser" },
        { value: "basis_plus_rd", label: "Basis + × RD" },
        { value: "basis_plus_nh", label: "Basis + × NH" },
        { value: "pruefzeit_stundensatz", label: "Prüfzeit × Stdns." },
        { value: "anlagendaten", label: "Aus Anlagendaten" },
        { value: "kw_staffel", label: "kW-Staffel" },
        { value: "einzelpreis_staffel", label: "Einzelpreis-Staffel" },
        { value: "auftragsabhaengig", label: "Auftragsabhängig" },
      ],
      // Anlagendaten
      anlagendaten: [],
      anlagenFilter: "",
      anlagenSearch: "",
      anlagenPage: 1,
      anlagenPageSize: 50,
      anlagenSort: { field: "hersteller", asc: true },
      // Einzelpreise
      einzelpreise: [],
      zuschlagKeys: ["nh_ab_100", "nh_ab_130", "rd_ab_100", "rd_ab_130", "rd_ab_150"],
      // Nebenkosten
      nebenkostenSaetze: {
        verpflegung_ganztag: 0,
        verpflegung_halbtag: 0,
        reisezeit_pro_h: 0,
        uebernachtung_pro_nacht: 0,
        km_pauschale: 0,
      },
      // Berechnungssätze
      berechnungsSaetze: { stundensatz: 0, tagessatz: 0 },
      // Dropdowns
      dropdowns: { hersteller: [], produkte: [] },
    };
  },
  computed: {
    loading_page() {
      return this.$store.state.loading_page && this.$store.state.loading_page === "setup";
    },
    isNewFormat() {
      return this.berechnung && this.berechnung.length > 0;
    },
    readOnly() {
      return !!(this.preisliste.freigegeben);
    },
    tabs() {
      return [
        { key: "berechnung", label: this.$t("dm_pl_tab_berechnung") },
        { key: "anlagendaten", label: this.$t("dm_pl_tab_anlagendaten") },
        { key: "einzelpreise", label: this.$t("dm_pl_tab_einzelpreise") },
        { key: "nebenkosten", label: this.$t("dm_pl_tab_nebenkosten") },
        { key: "berechnungssaetze", label: this.$t("dm_pl_tab_berechnungssaetze") },
      ];
    },
    herstellerList() {
      return (this.dropdowns && this.dropdowns.hersteller) || [];
    },
    filteredAnlagen() {
      var list = this.anlagendaten;
      if (this.anlagenFilter) {
        list = list.filter(function(a) { return a.hersteller === this.anlagenFilter; }.bind(this));
      }
      if (this.anlagenSearch) {
        var q = this.anlagenSearch.toLowerCase();
        list = list.filter(function(a) {
          return (a.hersteller || "").toLowerCase().indexOf(q) > -1 ||
                 (a.typ || "").toLowerCase().indexOf(q) > -1;
        });
      }
      var field = this.anlagenSort.field;
      var asc = this.anlagenSort.asc;
      list = list.slice().sort(function(a, b) {
        var va = a[field], vb = b[field];
        if (va == null) va = "";
        if (vb == null) vb = "";
        if (typeof va === "string") va = va.toLowerCase();
        if (typeof vb === "string") vb = vb.toLowerCase();
        if (va < vb) return asc ? -1 : 1;
        if (va > vb) return asc ? 1 : -1;
        return 0;
      });
      return list;
    },
    anlagenPageCount() {
      return Math.max(1, Math.ceil(this.filteredAnlagen.length / this.anlagenPageSize));
    },
    paginatedAnlagen() {
      var start = (this.anlagenPage - 1) * this.anlagenPageSize;
      return this.filteredAnlagen.slice(start, start + this.anlagenPageSize);
    },
    nebenkostenFields() {
      return [
        { key: "verpflegung_ganztag", label: this.$t("dm_pl_label_verpflegung_ganztag"), unit: "EUR/Tag", step: 1 },
        { key: "verpflegung_halbtag", label: this.$t("dm_pl_label_verpflegung_halbtag"), unit: "EUR/Halbtag", step: 1 },
        { key: "reisezeit_pro_h", label: this.$t("dm_pl_label_reisezeit"), unit: "EUR/(h*Team)", step: 1 },
        { key: "uebernachtung_pro_nacht", label: this.$t("dm_pl_label_uebernachtung"), unit: "EUR/Nacht", step: 1 },
        { key: "km_pauschale", label: this.$t("dm_pl_label_km_pauschale"), unit: "EUR/km", step: 0.01 },
      ];
    },
  },
  watch: {
    anlagenFilter: function() { this.anlagenPage = 1; },
    anlagenSearch: function() { this.anlagenPage = 1; },
  },
  created() {
    this.loadPreisliste();
  },
  methods: {
    loadPreisliste() {
      var id = this.$route.params.id;
      if (!id) return;
      this.$store.commit("set_loading_page", "preisliste");
      getPreisliste(id)
        .then(function(d) {
          this.$store.commit("set_loading_page", null);
          if (d.data && d.data.preisliste) {
            var pl = d.data.preisliste;
            this.preisliste = pl;
            this.berechnung = pl.berechnung || [];
            this.anlagendaten = pl.anlagendaten || [];
            this.einzelpreise = pl.einzelpreise || [];
            this.nebenkostenSaetze = Object.assign({}, this.nebenkostenSaetze, pl.nebenkosten_saetze || {});
            this.berechnungsSaetze = Object.assign({}, this.berechnungsSaetze, pl.berechnungs_saetze || {});
            this.dropdowns = pl.dropdowns || { hersteller: [], produkte: [] };
            this.buildBerechnungGroups();
          }
          this.loaded = true;
        }.bind(this))
        .catch(function(e) {
          this.$store.commit("set_loading_page", null);
          this.$store.dispatch("api_error", e);
          this.loaded = true;
        }.bind(this));
    },
    buildBerechnungGroups() {
      var groups = [];
      var map = {};
      for (var i = 0; i < this.berechnung.length; i++) {
        var item = this.berechnung[i];
        var key = item.produkt || "";
        if (!map[key]) {
          map[key] = { produkt: key, collapsed: false, items: [] };
          groups.push(map[key]);
        }
        map[key].items.push(item);
      }
      this.berechnungGroups = groups;
    },
    flattenBerechnung() {
      var result = [];
      for (var g = 0; g < this.berechnungGroups.length; g++) {
        for (var i = 0; i < this.berechnungGroups[g].items.length; i++) {
          result.push(this.berechnungGroups[g].items[i]);
        }
      }
      return result;
    },
    formelTypLabel(typ) {
      var found = this.formelTypen.find(function(ft) { return ft.value === typ; });
      return found ? found.label : typ || "—";
    },
    formelHasFaktor(typ) {
      return ["stundensatz_faktor", "nh_faktor", "rd_faktor", "basis_plus_rd", "basis_plus_nh", "kw_staffel"].indexOf(typ) > -1;
    },
    formelHasBasis(typ) {
      return ["basis_plus_rd", "basis_plus_nh", "festpreis", "kw_staffel"].indexOf(typ) > -1;
    },
    addBerechnung(gIdx) {
      var group = this.berechnungGroups[gIdx];
      group.items.push({
        produkt: group.produkt,
        modul: "",
        detail: "",
        preis: 0,
        preis_typ: "fest",
        formel_typ: "festpreis",
        formel_faktor: 0,
        formel_basis: 0,
        pruefungstypen: { ibn: "", eow: "", wkp_basic: "", wkp_plus: "", zop: "" },
      });
    },
    removeBerechnung(gIdx, iIdx) {
      this.berechnungGroups[gIdx].items.splice(iIdx, 1);
    },
    sortAnlagen(field) {
      if (this.anlagenSort.field === field) {
        this.anlagenSort.asc = !this.anlagenSort.asc;
      } else {
        this.anlagenSort.field = field;
        this.anlagenSort.asc = true;
      }
    },
    addAnlage() {
      this.anlagendaten.push({
        hersteller: "", typ: "", durchmesser: 0, kw: 0, turmtyp: "",
        trafo_position: "", pruefzeit: 0, blitzschutz_pauschale: 150,
        getriebe: "y", festpreis_rb_aussen: 0, festpreis_rb_innen: 0,
        alter_preis: 0, preis_ve: "", nabenhoehen: [],
      });
      // Jump to last page
      this.anlagenPage = Math.ceil(this.anlagendaten.length / this.anlagenPageSize);
    },
    removeAnlage(anlage) {
      var idx = this.anlagendaten.indexOf(anlage);
      if (idx > -1) this.anlagendaten.splice(idx, 1);
    },
    parseNabenhoehen(anlage, evt) {
      var val = evt.target.value || "";
      anlage.nabenhoehen = val.split(",").map(function(s) { return parseFloat(s.trim()); }).filter(function(n) { return !isNaN(n); });
    },
    addStaffel(epIdx) {
      this.einzelpreise[epIdx].staffeln.push({
        anzahl: this.einzelpreise[epIdx].staffeln.length + 1,
        basispreis: 0,
        zuschlaege: { nh_ab_100: 0, nh_ab_130: 0, rd_ab_100: 0, rd_ab_130: 0, rd_ab_150: 0 },
      });
    },
    removeStaffel(epIdx, sIdx) {
      this.einzelpreise[epIdx].staffeln.splice(sIdx, 1);
    },
    addEinzelpreisGruppe() {
      this.einzelpreise.push({
        bezeichnung: "",
        staffeln: [{ anzahl: 1, basispreis: 0, zuschlaege: { nh_ab_100: 0, nh_ab_130: 0, rd_ab_100: 0, rd_ab_130: 0, rd_ab_150: 0 } }],
      });
    },
    removeEinzelpreisGruppe(idx) {
      this.einzelpreise.splice(idx, 1);
    },
    formatPrice(val) {
      if (val == null || val === "") return "—";
      return Number(val).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    savePreisliste() {
      this.errorMessage = "";
      this.successMessage = "";
      var payload = {
        id: this.preisliste._id,
        preisliste: {
          berechnung: this.flattenBerechnung(),
          anlagendaten: this.anlagendaten,
          einzelpreise: this.einzelpreise,
          nebenkosten_saetze: this.nebenkostenSaetze,
          berechnungs_saetze: this.berechnungsSaetze,
          dropdowns: this.dropdowns,
        },
      };
      this.$store.commit("set_loading_page", "preisliste");
      updatePreisliste(payload.id, payload.preisliste)
        .then(function(d) {
          this.$store.commit("set_loading_page", null);
          if (d && d.status === "error") {
            this.errorMessage = d.message || "Fehler beim Speichern";
          } else {
            this.successMessage = this.$t("dm_pl_msg_saved");
            setTimeout(function() { this.successMessage = ""; }.bind(this), 3000);
            if (d && d.data && d.data.preisliste) {
              this.preisliste = d.data.preisliste;
            }
          }
        }.bind(this))
        .catch(function(e) {
          this.$store.commit("set_loading_page", null);
          this.errorMessage = "Fehler beim Speichern";
          this.$store.dispatch("api_error", e);
        }.bind(this));
    },
    onReimport(evt) {
      var file = evt.target.files && evt.target.files[0];
      if (!file) return;
      this.$refs.reimportFile.value = "";
      var self = this;
      this.$swal({
        title: this.$t("dm_preislisten_confirm_reimport_title"),
        text: this.$t("dm_preislisten_confirm_reimport_text"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t("dm_preislisten_btn_reimport_yes"),
        cancelButtonText: this.$t("dm_common_btn_cancel"),
      }).then(function(result) {
        if (result.isConfirmed) {
          self.$store.commit("set_loading_page", "preisliste");
          importPreisliste(file)
            .then(function(d) {
              self.$store.commit("set_loading_page", null);
              if (d && d.status === "error") {
                self.$swal({ title: self.$t("dm_preislisten_import_error"), text: d.message, icon: "error" });
                return;
              }
              var pl = (d && d.data && d.data.preisliste) || {};
              if (pl._id) {
                self.$router.replace("/dm/preisliste/" + pl._id);
                self.loadPreisliste();
              }
            })
            .catch(function(e) {
              self.$store.commit("set_loading_page", null);
              self.$swal({ title: self.$t("dm_preislisten_import_error"), text: e.message, icon: "error" });
            });
        }
      });
    },
  },
};
</script>

<style scoped>
.dm-group-header {
  padding: 10px 12px;
  background: rgba(128, 128, 128, 0.08);
  border: 1px solid rgba(128, 128, 128, 0.15);
  border-radius: 4px;
  cursor: pointer;
  user-select: none;
  margin-bottom: 8px;
}

.dm-group-header:hover {
  background: rgba(128, 128, 128, 0.14);
}

.dm-table-wrap {
  overflow: auto;
}

.dm-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.dm-table-compact td,
.dm-table-compact th {
  padding: 6px 8px;
}

.dm-table thead {
  background: rgba(128, 128, 128, 0.08);
  border-bottom: 2px solid rgba(128, 128, 128, 0.15);
  position: sticky;
  top: 0;
  z-index: 1;
}

.dm-table-th {
  text-align: left;
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: 0.6;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}

.dm-table-th:hover {
  opacity: 0.85;
}

.dm-sort-icon {
  font-size: 10px;
  margin-left: 4px;
  color: #E8751A;
}

.dm-table tbody td {
  border-bottom: 1px solid rgba(128, 128, 128, 0.1);
}

.dm-table tbody tr:hover {
  background: rgba(128, 128, 128, 0.06);
}

.dm-select-mini {
  padding: 2px 4px;
  font-size: 12px;
  min-width: 50px;
}

.nav-tabs .nav-link {
  font-size: 14px;
  font-weight: 500;
}

.nav-tabs .nav-link.active {
  color: #E8751A;
}

.form-control-plaintext {
  padding: 0.375rem 0;
  font-weight: 500;
}
</style>
